import React, { useState, useEffect } from 'react';
import { CreditCard, DollarSign, Zap, AlertCircle, Check, Clock, Settings, Loader2, RefreshCw, BookOpen, TrendingUp, Activity } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import api from '@/lib/api';
import PayPalSetupGuide from './PayPalSetupGuide';
import MrrTrendChart from './MrrTrendChart';

const STATUS_BADGE = {
  operational: { bg: 'bg-emerald-900/30', text: 'text-emerald-200', dot: 'bg-green-500', label: 'Operational' },
  degraded: { bg: 'bg-yellow-900/30', text: 'text-yellow-200', dot: 'bg-yellow-500', label: 'Degraded' },
  'not-configured': { bg: 'bg-[#131C3A]', text: 'text-slate-100', dot: 'bg-gray-400', label: 'Not Configured' },
  'configured-not-exposed': { bg: 'bg-blue-900/30', text: 'text-blue-200', dot: 'bg-blue-500', label: 'Configured (hidden)' },
  live: { bg: 'bg-emerald-900/30', text: 'text-emerald-200', dot: 'bg-green-500', label: 'Live' },
};

const formatUSD = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);
const formatDate = (iso) => iso ? new Date(iso).toLocaleString() : '—';

const StatusBadge = ({ status }) => {
  const s = STATUS_BADGE[status] || STATUS_BADGE['not-configured'];
  return (
    <Badge className={`${s.bg} ${s.text} hover:${s.bg}`}>
      <span className={`w-2 h-2 ${s.dot} rounded-full mr-1.5`}></span>
      {s.label}
    </Badge>
  );
};

const MetricCard = ({ label, value, sub, icon: Icon, accent = 'text-red-400' }) => (
  <Card>
    <CardContent className="p-5">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-500 uppercase tracking-wide">{label}</span>
        {Icon ? <Icon className={`h-4 w-4 ${accent}`} /> : null}
      </div>
      <div className={`text-2xl font-bold ${accent}`}>{value}</div>
      {sub ? <div className="text-xs text-gray-500 mt-1">{sub}</div> : null}
    </CardContent>
  </Card>
);

const PaymentsPage = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [status, setStatus] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [chart, setChart] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [flags, setFlags] = useState({ paypal_enabled: false });
  const [savingFlag, setSavingFlag] = useState(false);
  const [showPaypalGuide, setShowPaypalGuide] = useState(false);

  const fetchAll = async (showToast = false) => {
    try {
      setRefreshing(true);
      const [statusRes, analyticsRes, chartRes, txRes, flagsRes] = await Promise.all([
        api.get('/admin/payments/status'),
        api.get('/admin/payments/analytics'),
        api.get('/admin/payments/revenue-chart?days=30'),
        api.get('/admin/payments/transactions?limit=10'),
        api.get('/admin/payments/feature-flags'),
      ]);
      setStatus(statusRes.data);
      setAnalytics(analyticsRes.data);
      setChart(chartRes.data);
      setTransactions(txRes.data.items || []);
      const paypalFlag = (flagsRes.data.flags || []).find(f => f.key === 'paypal_enabled');
      setFlags({ paypal_enabled: !!paypalFlag?.value });
      if (showToast) toast.success('Payment data refreshed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load payment data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchAll(false); }, []);

  const togglePaypalFlag = async (val) => {
    try {
      setSavingFlag(true);
      await api.patch('/admin/payments/feature-flags/paypal_enabled', { value: val });
      setFlags(f => ({ ...f, paypal_enabled: val }));
      toast.success(`PayPal user option ${val ? 'enabled' : 'disabled'}`);
      fetchAll(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update flag');
    } finally {
      setSavingFlag(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[300px]">
        <Loader2 className="h-8 w-8 animate-spin text-red-400" />
      </div>
    );
  }

  const maxRev = Math.max(...(chart?.series || []).map(p => p.revenue), 1);

  return (
    <div className="p-8">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-100 mb-2" data-testid="payments-title">Payment Management</h1>
          <p className="text-slate-300">Live status, analytics, transactions, and integration controls.</p>
        </div>
        <Button variant="outline" onClick={() => fetchAll(true)} disabled={refreshing} data-testid="refresh-payments">
          <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* ============ HIGH-LEVEL METRICS ============ */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <MetricCard label="MRR" value={formatUSD(analytics?.mrr)} sub={`${analytics?.subscriptions?.active_paying ?? analytics?.subscriptions?.active ?? 0} paying subscribers`} icon={TrendingUp} />
        <MetricCard label="ARR" value={formatUSD(analytics?.arr)} sub="Annualized run rate" icon={TrendingUp} accent="text-[#16A34A]" />
        <MetricCard label="Last 30d Revenue" value={formatUSD(analytics?.revenue?.last_30d)} sub={`${analytics?.transactions?.succeeded_total || 0} successful payments`} icon={DollarSign} accent="text-[#0EA5E9]" />
        <MetricCard label="Churn Rate" value={`${analytics?.metrics?.churn_rate_pct?.toFixed(1) || 0}%`} sub={`Trial→Paid: ${analytics?.metrics?.trial_to_paid_rate_pct?.toFixed(1) || 0}%`} icon={Activity} accent="text-[#DC2626]" />
      </div>

      <Tabs defaultValue="status" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="status" data-testid="tab-status">Integration Status</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
          <TabsTrigger value="transactions" data-testid="tab-transactions">Transactions</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
        </TabsList>

        {/* ============ INTEGRATION STATUS TAB ============ */}
        <TabsContent value="status" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Stripe */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="bg-[#635BFF]/10 p-3 rounded-full"><CreditCard className="h-6 w-6 text-[#635BFF]" /></div>
                    <div><CardTitle className="text-slate-100">Stripe Integration</CardTitle><CardDescription>Primary payment processor</CardDescription></div>
                  </div>
                  <StatusBadge status={status?.stripe?.configured ? 'operational' : 'not-configured'} />
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-300">API Configuration</span><Badge className="bg-emerald-900/30 text-emerald-300">{status?.stripe?.configured ? 'Connected' : 'Missing key'}</Badge></div>
                <div className="flex justify-between"><span className="text-slate-300">Checkout Sessions</span><Badge className="bg-emerald-900/30 text-emerald-300">{status?.stripe?.checkout_active ? 'Active' : 'Inactive'}</Badge></div>
                <div className="flex justify-between"><span className="text-slate-300">Subscription Plans</span><Badge variant="secondary" className="text-red-400">Starter $49 · Pro $99 · Business $299 · Enterprise Custom</Badge></div>
                <p className="text-xs text-slate-400 pt-1">Full pricing breakdown on the <strong>Settings</strong> tab.</p>
              </CardContent>
            </Card>

            {/* PayPal */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="bg-[#0070BA]/10 p-3 rounded-full"><DollarSign className="h-6 w-6 text-[#0070BA]" /></div>
                    <div><CardTitle className="text-slate-100">PayPal Integration</CardTitle><CardDescription>Alternative payment method</CardDescription></div>
                  </div>
                  <StatusBadge status={status?.paypal?.status || 'not-configured'} />
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="bg-blue-950/40 border border-blue-800/50 rounded-lg p-3 text-sm text-blue-200 flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>PayPal SDK is scaffolded. Add `PAYPAL_CLIENT_ID`, `PAYPAL_CLIENT_SECRET`, `PAYPAL_WEBHOOK_ID`, `PAYPAL_MODE` to env to enable. Use the toggle in Settings to expose PayPal to end users once credentials are live.</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-slate-300">API Configuration</span><Badge variant="secondary">{status?.paypal?.configured ? 'Configured' : 'Not Configured'}</Badge></div>
                  <div className="flex justify-between"><span className="text-slate-300">Exposed to Users</span><Badge variant="secondary">{status?.paypal?.user_enabled ? 'Yes (flag on)' : 'No'}</Badge></div>
                </div>
                <Button variant="outline" className="w-full" onClick={() => setShowPaypalGuide(true)} data-testid="paypal-setup-guide-btn">
                  <BookOpen className="mr-2 h-4 w-4" /> View PayPal Setup Guide
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* API Status Panel (live) */}
          <Card>
            <CardHeader>
              <CardTitle className="text-slate-100 flex items-center"><Zap className="mr-2 h-5 w-5" /> Payment API Status</CardTitle>
              <CardDescription>Live status of payment endpoints — derived from real telemetry</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium">Subscription Creation</h4>
                    <StatusBadge status={status?.subscription_creation?.status} />
                  </div>
                  <p className="text-sm text-slate-300 mb-2 font-mono">{status?.subscription_creation?.endpoint}</p>
                </div>
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium">Payment Processing</h4>
                    <StatusBadge status={status?.payment_processing?.status} />
                  </div>
                  <p className="text-sm text-slate-300 mb-2 font-mono">{status?.payment_processing?.endpoint}</p>
                  <p className="text-xs text-gray-500">Last payment: {formatDate(status?.payment_processing?.last_payment_at)}</p>
                </div>
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium">Webhook Handling</h4>
                    <StatusBadge status={status?.webhook_handling?.status} />
                  </div>
                  <p className="text-sm text-slate-300 mb-2 font-mono">{status?.webhook_handling?.endpoint}</p>
                  <p className="text-xs text-gray-500">Events 24h: <strong>{status?.webhook_handling?.events_last_24h ?? 0}</strong></p>
                  <p className="text-xs text-gray-500">Last event: {status?.webhook_handling?.last_event_type || '—'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============ ANALYTICS TAB ============ */}
        <TabsContent value="analytics" className="space-y-6">
          {/* iter164 — MRR rolling trend chart at top of Analytics tab */}
          <MrrTrendChart />

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <MetricCard label="Revenue 7d" value={formatUSD(analytics?.revenue?.last_7d)} icon={DollarSign} />
            <MetricCard label="Revenue 30d" value={formatUSD(analytics?.revenue?.last_30d)} icon={DollarSign} />
            <MetricCard label="Revenue 90d" value={formatUSD(analytics?.revenue?.last_90d)} icon={DollarSign} />
            <MetricCard label="Revenue 365d" value={formatUSD(analytics?.revenue?.last_365d)} icon={DollarSign} />
          </div>

          {/* Subscription breakdown */}
          <Card>
            <CardHeader><CardTitle>Subscription Distribution</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="border rounded-lg p-4 text-center"><div className="text-2xl font-bold text-emerald-400">{analytics?.subscriptions?.active || 0}</div><div className="text-xs text-gray-500 mt-1">Active</div></div>
                <div className="border rounded-lg p-4 text-center"><div className="text-2xl font-bold text-blue-400">{analytics?.subscriptions?.trial || 0}</div><div className="text-xs text-gray-500 mt-1">Trial</div></div>
                <div className="border rounded-lg p-4 text-center"><div className="text-2xl font-bold text-orange-400">{analytics?.subscriptions?.expired || 0}</div><div className="text-xs text-gray-500 mt-1">Expired</div></div>
                <div className="border rounded-lg p-4 text-center"><div className="text-2xl font-bold text-amber-400">{analytics?.subscriptions?.cancelled || 0}</div><div className="text-xs text-gray-500 mt-1">Cancelled</div></div>
              </div>
            </CardContent>
          </Card>

          {/* Revenue Chart (simple bars) */}
          <Card>
            <CardHeader>
              <CardTitle>Revenue — Last 30 Days</CardTitle>
              <CardDescription>Daily revenue from successful Stripe charges</CardDescription>
            </CardHeader>
            <CardContent>
              {(chart?.series?.length || 0) === 0 ? (
                <div className="text-sm text-gray-500 py-8 text-center">No revenue events recorded in the last 30 days.</div>
              ) : (
                <div className="flex items-end gap-1 h-44">
                  {chart.series.map((pt) => {
                    const h = pt.revenue > 0 ? Math.max((pt.revenue / maxRev) * 100, 4) : 1;
                    return (
                      <div key={pt.date} className="flex-1 flex flex-col items-center justify-end group relative">
                        <div className="absolute -top-7 opacity-0 group-hover:opacity-100 text-xs bg-gray-900 text-white px-2 py-1 rounded whitespace-nowrap z-10">
                          {pt.date}: {formatUSD(pt.revenue)} · {pt.transactions} tx
                        </div>
                        <div className="w-full bg-[#DC2626] rounded-t hover:bg-[#3a7ec0] transition-colors" style={{ height: `${h}%` }} />
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="flex justify-between text-xs text-gray-500 mt-2">
                <span>{chart?.series?.[0]?.date}</span>
                <span>{chart?.series?.[chart.series.length - 1]?.date}</span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============ TRANSACTIONS TAB ============ */}
        <TabsContent value="transactions">
          <Card>
            <CardHeader>
              <CardTitle>Recent Transactions</CardTitle>
              <CardDescription>Latest payment events recorded via Stripe webhook</CardDescription>
            </CardHeader>
            <CardContent>
              {transactions.length === 0 ? (
                <div className="text-sm text-gray-500 py-8 text-center">No transactions yet. Once Stripe webhooks fire on real payments, they&apos;ll appear here.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs text-gray-500 uppercase border-b">
                      <tr>
                        <th className="text-left py-2 px-3">Date</th>
                        <th className="text-left py-2 px-3">User</th>
                        <th className="text-left py-2 px-3">Provider</th>
                        <th className="text-right py-2 px-3">Amount</th>
                        <th className="text-center py-2 px-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((tx, i) => (
                        <tr key={tx.stripe_invoice_id || `tx-${i}`} className="border-b hover:bg-[#0A1024]">
                          <td className="py-2 px-3 text-slate-200">{formatDate(tx.created_at)}</td>
                          <td className="py-2 px-3 text-slate-200">{tx.user_email || '—'}</td>
                          <td className="py-2 px-3"><Badge variant="outline">{tx.provider}</Badge></td>
                          <td className="py-2 px-3 text-right font-medium">{formatUSD(tx.amount)}</td>
                          <td className="py-2 px-3 text-center">
                            <Badge className={tx.status === 'succeeded' ? 'bg-emerald-900/30 text-emerald-200' : 'bg-amber-900/30 text-amber-200'}>
                              {tx.status}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============ SETTINGS TAB (feature flags) ============ */}
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>Payment Feature Flags</CardTitle>
              <CardDescription>Admin-only toggles. Use these to gate features without redeploys.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h3 className="font-medium mb-1">Expose PayPal to end users</h3>
                  <p className="text-sm text-slate-300">When ON, the user-facing subscription page shows a &quot;Pay with PayPal&quot; button alongside Stripe. Requires PayPal credentials to be configured in the backend env.</p>
                  {!status?.paypal?.configured && (
                    <p className="text-xs text-orange-400 mt-1">⚠️ PayPal credentials not detected — toggle will not function until env vars are set.</p>
                  )}
                </div>
                <Switch
                  checked={flags.paypal_enabled}
                  onCheckedChange={togglePaypalFlag}
                  disabled={savingFlag}
                  data-testid="toggle-paypal-flag"
                />
              </div>
            </CardContent>
          </Card>

          {/* Current pricing display kept for reference */}
          <Card className="mt-6">
            <CardHeader><CardTitle>Pricing Structure</CardTitle></CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-lg">Starter</h4>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-red-400">$49</div>
                      <div className="text-sm text-slate-300">/month</div>
                    </div>
                  </div>
                  <div className="text-xs text-slate-400">1,500 AI calls · 1 seat</div>
                </div>
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-lg">Pro</h4>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-red-400">$99</div>
                      <div className="text-sm text-slate-300">/month</div>
                    </div>
                  </div>
                  <div className="text-xs text-slate-400">5,000 AI calls · 2 seats (shared)</div>
                </div>
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-lg">Business</h4>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-red-400">$299</div>
                      <div className="text-sm text-slate-300">/month</div>
                    </div>
                  </div>
                  <div className="text-xs text-slate-400">10,000 AI calls · 5 seats (shared)</div>
                </div>
                <div className="border rounded-lg p-4 opacity-90">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-lg">Enterprise</h4>
                    <div className="text-right"><div className="text-2xl font-bold text-gray-400">Custom</div><div className="text-sm text-slate-300">pricing</div></div>
                  </div>
                  <div className="text-xs text-slate-400">Custom AI + seats · SLA · Contact sales</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* PayPal Setup Guide modal */}
      <PayPalSetupGuide open={showPaypalGuide} onClose={() => setShowPaypalGuide(false)} />
    </div>
  );
};

export default PaymentsPage;
